import { useState, useEffect, useCallback } from 'react'
import { Sparkles, Mail, Loader2, Check, X } from 'lucide-react'
import api from '../../services/api'
import { formatDateTime } from '../../utils/helpers'

/**
 * Work the agent has prepared and is waiting to be told to do.
 *
 * WHY THIS SCREEN EXISTS. A proposal made in the chat panel is confirmed in that conversation.
 * A proposal that arrives from an EMAIL has no conversation to sit in — it was a row in a table
 * nobody could see, so somebody could email their deliverable, be told it was ready, and have no
 * way anywhere to say yes. This is where those land.
 *
 * NOTHING HERE HAS HAPPENED YET. Every card is an action prepared and not run. That is the whole
 * point of the card: the agent did the finding, the reading and the arithmetic, and a person
 * still decides. Discarding is offered as plainly as confirming, because saying no has to be as
 * easy as saying yes — otherwise the only way to refuse is to let it expire, which reads as the
 * system waiting on you rather than you having decided.
 *
 * Renders nothing when there is nothing waiting.
 */
export default function AgentProposals() {
  const [items, setItems] = useState([])
  const [busy, setBusy] = useState(null)
  const [note, setNote] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/ai/agent/pending')
      setItems(data?.items || [])
    } catch {
      // A list that will not load must not take the dashboard with it.
      setItems([])
    }
  }, [])

  useEffect(() => { load() }, [load])

  const confirm = async (item) => {
    setBusy(item.confirm_token); setError(null); setNote(null)
    try {
      const form = new FormData()
      form.append('message', 'confirm')
      form.append('confirm_token', item.confirm_token)
      // The SAME endpoint the chat panel confirms through, so the action runs exactly one way
      // however it was proposed.
      const { data } = await api.post('/ai/agent', form)
      if (data?.error) {
        // The proposal is single-use and has already been consumed, so there is nothing left
        // here to press again. Say that, rather than leaving them clicking a button that is gone.
        setError(`${data.error} Check Deliverables before sending it again \u2014 if you also replied CONFIRM by email it may already be done.`)
      } else {
        setNote(data?.answer || 'Done.')
      }
      await load()
    } catch (e) {
      setError(e.response?.data?.detail || 'That could not be completed — please try again.')
    } finally { setBusy(null) }
  }

  const discard = async (item) => {
    if (!window.confirm('Throw this away without doing it?')) return
    setBusy(item.confirm_token); setError(null); setNote(null)
    try {
      const { data } = await api.delete(`/ai/agent/pending/${item.confirm_token}`)
      setNote(data?.detail || 'Discarded.')
      await load()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not discard that.')
    } finally { setBusy(null) }
  }

  // AN ERROR MUST OUTLIVE THE THING THAT CAUSED IT. Confirming consumes the proposal, so the
  // list empties — and this guard used to hide the card the moment it did, taking the failure
  // message with it. The card vanished and said nothing, which reads as the button doing
  // nothing at all. Anything we have to tell them keeps the card on screen.
  if (items.length === 0 && !note && !error) return null

  return (
    <div className="bg-violet-50 border border-violet-200 rounded-xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-violet-600" />
        <h3 className="font-semibold text-violet-900">
          Ready to go — waiting for you
        </h3>
      </div>
      <p className="text-xs text-violet-700 mb-3">
        The agent has prepared {items.length === 1 ? 'this' : 'these'} and has <b>not</b> done
        {items.length === 1 ? ' it' : ' them'} yet.
      </p>

      {note && <div className="mb-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{note}</div>}
      {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      <div className="space-y-2">
        {items.map(item => (
          <div key={item.confirm_token} className="bg-white border border-violet-200 rounded-lg p-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{item.summary || item.tool}</p>
                <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap">
                  {item.from_email && (
                    <span className="inline-flex items-center gap-1 text-violet-700">
                      <Mail className="w-3 h-3" />
                      {/* Where it came from. An action with no provenance is one nobody can
                          account for later. */}
                      from your email{item.subject ? ` — "${item.subject}"` : ''}
                    </span>
                  )}
                  {item.created_at && <span>· prepared {formatDateTime(item.created_at)}</span>}
                  {item.money && <span className="text-amber-700 font-medium">· involves money</span>}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => confirm(item)}
                  disabled={busy === item.confirm_token}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white"
                >
                  {busy === item.confirm_token
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Check className="w-3.5 h-3.5" />}
                  Confirm
                </button>
                <button
                  onClick={() => discard(item)}
                  disabled={busy === item.confirm_token}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" /> Discard
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
